import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SortingState } from "@tanstack/react-table";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  handleProductQuotationStatus,
  fetchAllProductQuotations,
  deleteProductQuotation,
  downloadProductQuotationPdf,
  fetchProductQuotationById,
  updateProductQuotationEmailDetails,
} from "slices/quotation";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import QuotationLogStatusDropdown from "Common/Components/StatusDropdown/quotationLogStatusDropdown";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { Button, Card, CardBody, Col, Form, Modal, Row } from "react-bootstrap";
import TextEditor from "components/TextEditor/TextEditor";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import FollowUpModal from "components/FollowUpModal/FollowUpPopUp";
import Chip from "components/Chips/Chip";
import FollowupModel from "components/FollowUpModal/FollowUpModel";
import { showConfirm, showError, showSuccess, showLoading, hideLoading } from "helpers/swalHelper";
import { toast } from "react-toastify";
import { Tooltip } from "@mui/material";
import DOMPurify from "dompurify";
import { exportProductQuotationCSV } from "slices/quotation/productThunk";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { QUOTATION_STAGE } from "Constants/StatusConstant";
import FormActionButtons from "pages/QuotationV2/Form/Components/FormActionButtons";
import { hideTagsSearchListingSection } from "../../utils/tagsSearchEmpty";

interface QuotationLogProps {
  noWrapper?: boolean;
  forceShowOnTagsSearch?: boolean;
}

const QuotationLog: React.FC<QuotationLogProps> = ({
  noWrapper = false,
  forceShowOnTagsSearch = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

    const tableState = usePreservedTableState({
      key: TableKeys.QUOTATION.PRODUCT.LOG,
      defaults: {
        pageIndex: 0,
        pageSize: 10,
        search: "",
        searchInput: "",
        sorting: [],
        columnFilters: [],
        filterValue: [],
      },
    });
  
    const {
      pageIndex,
      pageSize,
      search,
      searchInput,
      sorting,
      columnFilters,
      filterValue,
      setPageIndex,
      setPageSize,
      setSearch,
      setSearchInput,
      setSorting,
      setColumnFilters,
      setFilterValue,
      resetState,
    } = tableState;

  // State management
  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableInstance, setTableInstance] = useState<any>();
  const [data, setData] = useState<any[]>([]);

  // Follow Up Modal  states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedFollowUpItem, setSelectedFollowUpItem] = useState<any>(null);

  // Email Details (Download PDF) modal state
  const [showEmailDetailsModal, setShowEmailDetailsModal] = useState(false);
  const [emailDetailsQuotationId, setEmailDetailsQuotationId] = useState<string | null>(null);
  const [emailDetailsQuotationNumber, setEmailDetailsQuotationNumber] = useState<string>("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailDetailsLoading, setEmailDetailsLoading] = useState(false);
  const [emailDetailsSaving, setEmailDetailsSaving] = useState(false);

  // 🔍 If opened from /tags-search/:tags, always use URL tag as effective search.
  const { tags: tagsParam } = useParams<{ tags?: string }>();
  const tagFromUrl = useMemo(() => {
    if (!tagsParam) return "";
    try {
      return decodeURIComponent(tagsParam).trim();
    } catch {
      return tagsParam.trim();
    }
  }, [tagsParam]);
  const effectiveSearch = tagFromUrl || search;

  useEffect(() => {
    if (!tagFromUrl) return;
    setSearchInput(tagFromUrl);
    setSearch(tagFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFromUrl]);

  // Redux selectors
  const { loading, error: quotationError } = useSelector(
    (state: any) => state.Quotation,
  );

  const { canView, canUpdate } = usePermission(
    app_modules?.quotation?.app_name,
    app_modules?.quotation?.modules?.product
  );

  // Sorting parameters
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Effects
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  // Data fetching
  const handleFetchQuotationData = useCallback(async () => {
    try {
      const resData = await dispatch(
        fetchAllProductQuotations({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search: effectiveSearch,
          ...(tagFromUrl ? { search_type: "tag" } : {}),
          filters: filterValue,
          type: QUOTATION_STAGE.LOG,
        })
      ).unwrap();
      setData(resData?.data || []);
      setTotalRecords(resData?.rowCount || 0);
    } catch (err: any) {
      console.error("Error fetching quotations:", err);
      toast.error(err?.message || "Failed to fetch quotations");
      setData([]);
      setTotalRecords(0);
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    filterValue,
    effectiveSearch,
  ]);

  useEffect(() => {
    handleFetchQuotationData();
  }, [handleFetchQuotationData]);

  // Handlers
  const viewRoute = "/tags-search/quotation/product/view";

  const handleView = (quotationNo: string) => {
    navigate(`${viewRoute}/${quotationNo}`);
  };

  const handleEdit = (quotationNo: string) => {
    navigate(`/quotation/product/edit/${quotationNo}`);
  };

  const handleDelete = async (id: string, enquiryNumber?: string) => {
    const isFromEnquiry = Boolean(enquiryNumber);
    const result = await showConfirm(
      isFromEnquiry
        ? "This quotation will be reverted and the enquiry status will be changed back to 'In Progress'. Do you want to continue?"
        : "This quotation will be permanently deleted. Do you want to continue?",
      isFromEnquiry ? "Revert Quotation" : "Delete Quotation",
      isFromEnquiry ? "Yes, revert" : "Yes, delete",
      "Cancel"
    );

    if (!result.isConfirmed) return;

    try {
      await dispatch(deleteProductQuotation(id)).unwrap();
      toast.success(
        isFromEnquiry
          ? "Quotation reverted successfully"
          : "Quotation deleted successfully"
      );
      handleFetchQuotationData();
    } catch (err: any) {
      console.error("Error deleting quotation:", err);
      toast.error(err?.message || "Failed to delete quotation");
    }
  };

  const handleDownloadPdf = async (id: string, quotationNumber: string) => {
    try {
      showLoading("Downloading PDF...");
      await dispatch(
        downloadProductQuotationPdf({ id, quotationNumber })
      ).unwrap();
      hideLoading();
      showSuccess("PDF downloaded successfully");
    } catch (err: any) {
      hideLoading();
      console.error("Error downloading PDF:", err);
      showError(err?.message || "Failed to download PDF");
    }
  };

  const openEmailDetailsModal = async (id: string, quotationNumber: string) => {
    setEmailDetailsQuotationId(id);
    setEmailDetailsQuotationNumber(quotationNumber);
    setEmailSubject("");
    setEmailBody("");
    setShowEmailDetailsModal(true);
    setEmailDetailsLoading(true);
    try {
      const data = await dispatch(fetchProductQuotationById(id)).unwrap();
      setEmailSubject(data?.pdf_email_subject ?? "");
      setEmailBody(data?.pdf_email_body ?? "");
    } catch (err: any) {
      toast.error(err?.message || "Failed to load quotation details");
    } finally {
      setEmailDetailsLoading(false);
    }
  };

  const closeEmailDetailsModal = () => {
    setShowEmailDetailsModal(false);
    setEmailDetailsQuotationId(null);
    setEmailDetailsQuotationNumber("");
    setEmailSubject("");
    setEmailBody("");
  };

  const handleSaveAndDownloadPdf = async () => {
    if (!emailDetailsQuotationId) return;
    setEmailDetailsSaving(true);
    try {
      await dispatch(
        updateProductQuotationEmailDetails({
          id: emailDetailsQuotationId,
          pdf_email_subject: emailSubject || null,
          pdf_email_body: emailBody || null,
        })
      ).unwrap();
      await dispatch(
        downloadProductQuotationPdf({
          id: emailDetailsQuotationId,
          quotationNumber: emailDetailsQuotationNumber,
        })
      ).unwrap();
      showSuccess("Email details updated and PDF downloaded successfully");
      closeEmailDetailsModal();
    } catch (err: any) {
      showError(err?.message || "Failed to update or download PDF");
    } finally {
      setEmailDetailsSaving(false);
    }
  };

  const handleStatusChange = async (
    quotationId: string,
    newStatus: string,
    remark?: string
  ) => {
    try {
      await dispatch(
        handleProductQuotationStatus({
          id: quotationId,
          quotation_status: newStatus,
          remark: remark,
        })
      ).unwrap();
      handleFetchQuotationData();
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  // Columns definition
  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => row.original.sr_no || "-",
      // },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "quotation_number",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.quotation_number || "-",
      },
      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        id: "quotation_date",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "quotation_date",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.quotation_date || "-"} />
        ),
      },
      {
        header: "Enquiry Ref",
        accessorKey: "enquiry_number",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "enquiry_number",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.enquiry_number || "-",
      },
      {
        header: "Company Name",
        accessorKey: "customer_name",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "customer_name",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.customer_name || "-",
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "agent_name",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.agent_name || "-",
      },
      {
        header: "Status",
        accessorKey: "quotation_status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "quotation_status",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => {
          const status = row.original.quotation_status;
          return (
            <QuotationLogStatusDropdown
              currentStatus={status}
              itemId={row.original.quotation_product_id}
              onStatusChange={handleStatusChange}
              disabled={false}
              requireRemarks={true}
            />
          );
        },
      },
      {
        header: "Assigned To",
        accessorKey: "assigned_members",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const members = row.original?.assigned_members || [];

          if (!members.length) return "-";

          const names = members.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Last Follow Up Date",
        accessorKey: "last_follow_up_date",
          enableColumnFilter:   false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate
            date={row.original?.quotation_follow_ups?.[0]?.follow_up_date}
          />
        ),
      },
      {
        header: "Follow Up Remark",
        accessorKey: "follow_up_remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const rawHtml =
            row.original?.quotation_follow_ups?.[0]?.follow_up_remarks || "";

          if (!rawHtml) return <span>-</span>;

          const safeHtml = DOMPurify.sanitize(rawHtml);

          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <div
                  style={{
                    maxWidth: "450px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 500,
                    p: 1.5,
                  },
                },
              }}
            >
              <div
                style={{
                  minWidth: "200px",
                  maxWidth: "400px",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  lineHeight: "1.5",
                  maxHeight: "6em", // 4 lines * 1.5 line-height
                  cursor: "pointer",
                }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </Tooltip>
          );
        },
      },
      {
        header: "Next Follow Up Date",
        accessorKey: "next_follow_up_date",
        id: "next_follow_up_date",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate
            date={row.original?.quotation_follow_ups?.[0]?.next_follow_up_date}
          />
        ),
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/ Date",
        accessorKey: "created_at",
        id: "created_at",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "created_at",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username || "-"}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/ Date",
        accessorKey: "updated_at",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.PRODUCT.FILTER_DROPDOWN(
          "updated_at",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user?.username || "-"}
            date={row.original.updated_at}
          />
        ),
      },
    ];

    const actionColumn = {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const quotaionID = row.original.quotation_product_id || row.original.id;
        const data = row.original;

        const allActions = [
          {
            label: "View",
            permission: canView,
            icon: <VisibilityIcon fontSize="small" color="info" />,
            onClick: () => handleView(quotaionID),
          },
          {
            label: "Edit",
            permission: canUpdate,
            icon: <EditIcon fontSize="small" color="primary" />,
            onClick: () => handleEdit(quotaionID),
          },
          {
            label: "Follow up",
            permission: canUpdate,
            icon: <PersonAddIcon fontSize="small" color="success" />,
            onClick: () => {
              setSelectedFollowUpItem(data);
              setShowFollowUpModal(true);
            },
          },
          {
            label: "Download PDF",
            permission: canView,
            icon: <InsertDriveFileIcon fontSize="small" color="primary" />,
            onClick: () => {
              openEmailDetailsModal(quotaionID, data.quotation_number);
            },
          },
          {
            label: data.enquiry_number ? "Revert" : "Delete",
            permission: canUpdate,
            icon: data.enquiry_number ? (
              <UndoIcon fontSize="small" color="warning" />
            ) : (
              <DeleteOutlineIcon fontSize="small" color="error" />
            ),
            onClick: () => handleDelete(quotaionID, data.enquiry_number),
            className: "text-danger",
          },
        ];

        // Filter actions based on quotation status
        const status = data.quotation_status;
        const deleteLabel = data.enquiry_number ? "Revert" : "Delete";
        let actions = allActions;

        if (status === "PO Received") {
          actions = allActions.filter(
            (action) =>
              action.label === "View" ||
              action.label === "Download PDF" ||
              action.label === "Follow up"
          );
        } else if (status === "Lost") {
          actions = allActions.filter(
            (action) =>
              action.label === "View" ||
              action.label === deleteLabel ||
              action.label === "Download PDF" ||
              action.label === "Follow up"
          );
        }
        // For "Sent" and "Prepared", show all actions (default behavior)

        return <DynamicMenuItem row={row.original} menuItems={actions} />;
      },
    };
    return [...commonColumns, actionColumn];
  };

  const tableNode = (
    <LoaderErrorWrapper loading={loading}>
      <MasterTableContainer
        columns={getColumns()}
        data={data}
        totalRecords={totalRecords}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        colValue={setFilterValue}
        sorting={sorting}
        onSortingChange={setSorting}
        manualSorting={true}
        isBordered={false}
        isPagination={true}
        isGlobalFilter={false}
        SearchPlaceholder="Search product quotations..."
        onTableReady={setTableInstance}
      />
    </LoaderErrorWrapper>
  );

  const followUpModal =
    showFollowUpModal ? (
      <FollowUpModal
        open={showFollowUpModal}
        onClose={() => {
          setShowFollowUpModal(false);
          handleFetchQuotationData();
        }}
        title={`Follow Up - ${selectedFollowUpItem?.quotation_number || ""}`}
      >
        <FollowupModel
          data={selectedFollowUpItem}
          activeType="QUOTATION_PRODUCT"
          onClose={() => {
            setShowFollowUpModal(false);
            handleFetchQuotationData();
          }}
        />
      </FollowUpModal>
    ) : null;

  const emailDetailsModal = (
    <Modal show={showEmailDetailsModal} onHide={closeEmailDetailsModal} centered>
      <Modal.Header closeButton>
        <Modal.Title>Email Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {emailDetailsLoading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter Subject."
                className="rounded"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Body</Form.Label>
              <TextEditor
                value={emailBody}
                onChange={setEmailBody}
                placeholder="Write a comment...."
                minHeightPx={120}
                maxHeightPx={200}
                blockImages={true}
              />
            </Form.Group>
            <div
              className="mb-0 mt-2"
              style={{
                marginLeft: "calc(-1 * var(--bs-modal-padding, 1rem))",
                marginRight: "calc(-1 * var(--bs-modal-padding, 1rem))",
              }}
            >
              <hr className="mb-0 w-100" />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <FormActionButtons
          onCancel={closeEmailDetailsModal}
          onSave={handleSaveAndDownloadPdf}
          showSaveDraft={false}
          saveButtonLabel="Save and Download PDF"
          isSaving={emailDetailsSaving || emailDetailsLoading}
          wrapperClassName="d-flex flex-wrap gap-2 justify-content-center w-100"
        />
      </Modal.Footer>
    </Modal>
  );

  if (
    hideTagsSearchListingSection(
      noWrapper,
      loading,
      totalRecords,
      data.length,
      quotationError,
    ) &&
    !forceShowOnTagsSearch
  ) {
    return null;
  }

  if (noWrapper) {
    return (
      <>
        {tableNode}
        {followUpModal}
        {emailDetailsModal}
      </>
    );
  }

  return (
    <>
      <Card className="px-3">
        <CardBody className="px-0">{tableNode}</CardBody>
      </Card>
      {followUpModal}
      {emailDetailsModal}
    </>
  );
};

export default QuotationLog;
